<script setup lang="ts">
// Comprovante de saque em PDF do Minha Conta V2 (ENG-848).
// O core devolve o PDF inteiro em base64 num único GET; o V1 monta com ele um
// `data:application/pdf;base64,...` direto no iframe (components/user/wallet/
// WithdrawDetailsDialog.vue). Aqui o base64 vira Blob + objectURL porque o
// Chrome bloqueia `data:` em navegação de topo — com Blob o mesmo src serve
// pro iframe E pro botão de baixar, sem carregar a string gigante duas vezes.
// Shell dos demais modais do painel (LogoutConfirmModal.vue): bottom sheet no
// mobile, card centralizado no desktop.
interface Props {
  // id do SAQUE (ITransaction.src_id) — não é o id da transação
  srcId?: number;
  // mostrados no cabeçalho pro usuário saber de qual saque é o comprovante
  amount?: string;
  date?: string;
}

const props = defineProps<Props>();

const isOpen = defineModel<boolean>({ required: true });

useBodyScrollLock(isOpen);

const isLoading = ref<boolean>(false);
const hasError = ref<boolean>(false);
const fileUrl = ref<string>('');

function base64ToObjectUrl(base64: string) {
  // tolera o base64 vindo já como data URI e com quebras de linha (o PDF do
  // core chega numa string só, mas atob() estoura com qualquer sujeira)
  const clean = base64.replace(/^data:[^,]*,/, '').replace(/\s/g, '');
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
}

function releaseFile() {
  if (fileUrl.value) {
    URL.revokeObjectURL(fileUrl.value);
    fileUrl.value = '';
  }
}

async function fetchReceipt() {
  if (!props.srcId || isLoading.value) return;

  const { ApiService } = useApiService();

  isLoading.value = true;
  hasError.value = false;
  releaseFile();

  try {
    const res = await ApiService.get<string | { data?: string }>(
      `/withdraw/${props.srcId}/generate`,
    );

    // a rota responde o base64 cru no corpo (é o que o V1 consome); o
    // `?? res.data.data` cobre a variante com envelope sem um segundo caminho
    const base64 =
      typeof res.data === 'string' ? res.data : (res.data?.data ?? '');

    if (!base64) {
      throw new Error('comprovante vazio');
    }

    fileUrl.value = base64ToObjectUrl(base64);
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

// busca ao abrir e sempre que o saque mudar (o modal é único e reaproveitado
// por todos os cards da lista)
watch(
  () => [isOpen.value, props.srcId] as const,
  ([open]) => {
    if (open) {
      fetchReceipt();

      return;
    }

    releaseFile();
    hasError.value = false;
  },
  { immediate: true },
);

onBeforeUnmount(releaseFile);

function close() {
  isOpen.value = false;
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="user-v2-withdraw-receipt-modal fixed inset-0 z-[100] flex items-end justify-center font-sans lg:items-center lg:p-[16px]"
      @dragstart.prevent
    >
      <div
        class="modal-backdrop absolute inset-0 bg-black/70"
        @click="close"
      />

      <!-- overflow-clip (não hidden): hidden vira scroll container e o foco
           dentro do sheet gera scroll fantasma no mobile -->
      <div
        class="modal-card relative flex h-[92vh] w-full flex-col overflow-clip rounded-t-[20px] bg-v2-background text-left lg:h-[min(85vh,760px)] lg:max-w-[560px] lg:rounded-[20px]"
      >
        <div
          class="border-solid flex shrink-0 flex-col gap-[2px] border-b border-white/10 px-[16px] py-[16px] pr-[64px]"
        >
          <p class="text-[18px] font-bold leading-[28px] text-white">
            Comprovante de saque
          </p>
          <p
            v-if="props.amount || props.date"
            class="truncate text-[12px] leading-[16px] text-v2-default-800"
          >
            <span v-if="props.amount">{{ props.amount }}</span>
            <span v-if="props.amount && props.date"> · </span>
            <span v-if="props.date">{{ props.date }}</span>
          </p>
        </div>

        <UserV2ModalCloseButton @close="close" />

        <div class="relative min-h-0 flex-1 bg-v2-default-200">
          <div
            v-if="isLoading"
            class="absolute inset-0 flex flex-col items-center justify-center gap-[12px] px-[16px] text-center"
          >
            <span
              class="border-solid receipt-spinner size-[28px] rounded-full border-[3px] border-white/15 border-t-v2-primary"
            />
            <p class="text-[13px] leading-[18px] text-v2-default-800">
              Gerando seu comprovante...
            </p>
          </div>

          <div
            v-else-if="hasError"
            class="absolute inset-0 flex flex-col items-center justify-center gap-[12px] px-[24px] text-center"
          >
            <p class="text-[14px] leading-[20px] text-white">
              Não foi possível gerar o comprovante agora.
            </p>
            <p class="text-[12px] leading-[16px] text-v2-default-800">
              Tente de novo em instantes.
            </p>
            <button
              class="mt-[4px] flex h-[40px] items-center justify-center rounded-[12px] bg-v2-primary px-[24px] text-[14px] text-v2-primary-foreground"
              type="button"
              @click="fetchReceipt"
            >
              Tentar de novo
            </button>
          </div>

          <iframe
            v-else-if="fileUrl"
            class="size-full border-0"
            :src="fileUrl"
            title="Comprovante de saque"
          />
        </div>

        <div
          v-if="fileUrl"
          class="border-solid shrink-0 border-t border-white/10 px-[16px] pb-[24px] pt-[12px]"
        >
          <a
            class="flex h-[44px] items-center justify-center gap-[8px] rounded-[12px] bg-v2-primary text-[14px] font-medium text-v2-primary-foreground"
            :download="`comprovante-saque-${props.srcId}.pdf`"
            :href="fileUrl"
          >
            <svg
              class="size-[16px]"
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              viewBox="0 0 24 24"
            >
              <path d="M12 4v11m0 0-4-4m4 4 4-4M5 19h14" />
            </svg>
            Baixar PDF
          </a>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
// main.scss reseta `transition` no app inteiro — animação via @keyframes
.modal-backdrop {
  animation: withdraw-receipt-fade 0.18s ease-out;
}

.modal-card {
  animation: withdraw-receipt-pop 0.2s ease-out;

  @media (max-width: 1023px) {
    animation-name: withdraw-receipt-slide-up;
  }
}

.receipt-spinner {
  animation: withdraw-receipt-spin 0.7s linear infinite;
}

@keyframes withdraw-receipt-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes withdraw-receipt-pop {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes withdraw-receipt-slide-up {
  from {
    opacity: 0;
    transform: translateY(100%);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes withdraw-receipt-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
